import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table, Tag,
  Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { Dayjs } from 'dayjs';
import PageWithFilters from '../components/page/page-with-filters';
import { PageWithFiltersContentContainer } from '../components/page/page.styles';
import { IUnnamedLot } from '../interfaces/IUnnamedLots';
import useGetAllUnnamedLots from '../hooks/useGetAllUnnamedLots';
import useToastContext from '../hooks/useToastContext';
import { CardBalanceType, CardBalanceTypeParser, CardType } from '../interfaces/card';
import { CitiesByStateByCountry, Countries, EXCLUDED_COUNTRIES, RealmRoles, StatesByCountry } from '../constants';
import useCreateUnnamedLot from '../hooks/useCreateUnnamedLot';
import withAuthorization from '../components/hocs/withAuthorization';
import useGetCardProducts from '../hooks/data/useGetCardProducts';
import type { ColumnsType } from 'antd/es/table';
import { FirstStepFormInputs } from '../features/card-holders/new/types/firstStepTypes';
import useGetCardAffinityGroups from '../hooks/CardAffinityGroup/useGetCardAffinityGroups';
const { RangePicker } = DatePicker;

const filterOption = (input: string, option?: { label: string; value: string }) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());


type ShippingType = {
  street: string,
  street_number: number,
  city: string,
  state: string,
  country: string,
  postal_code: string,
  floor: string,
  apt: string,
  extra_comment: string
}

type FulFillmentType = {
  tracking_id: string,
  code_currier: string,
  shipping: ShippingType
}

type NewUnnamedLotFormInputs = {
  lot_number: number,
  lot_size: number,
  card_type: CardType,
  card_product_id: string,
  initial_fourth_line: number,
  fulfillment: FulFillmentType,
  balance_type: CardBalanceType,
  card_affinity_group_id: string,
};

type UnnamedLotsFilterProps = {
  rangeDates: [Dayjs, Dayjs];
};

const SubmitButton = ({ form, loading }: { form: FormInstance; loading: boolean }) => {
  const [submittable, setSubmittable] = React.useState(false);

  // Watch all values
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [values]);

  return (
    <Col span={24} style={{ textAlign: 'center' }}>
      <Button type="primary" htmlType="submit" disabled={!submittable} loading={loading}>
        Crear
      </Button>
    </Col>
  );
};

const NewUnnamedLotForm = ({ onCancel, execute }) => {
  const toast = useToastContext();
  const [form] = Form.useForm();
  const country = Form.useWatch(['fulfillment', 'shipping', 'country'], form);
  const state = Form.useWatch(['fulfillment', 'shipping', 'state'], form);
  const { lotLoading: isLoading, lotError: error, createUnnamedLot } = useCreateUnnamedLot();
  const { cardProducts, isLoading: cardProductsLoading } = useGetCardProducts(1, 100);
  const { affinityGroups, loading} = useGetCardAffinityGroups(0, 100, null);

  const onSubmit = async (data: NewUnnamedLotFormInputs) => {
    try {
      const response = await createUnnamedLot({ ...data })
      if(response.status === 200) {
        toast.success('Exito', 'El lote se ha creado satisfactoriamente!.');
        form.resetFields();
        execute();
        onCancel();
      } else {
        throw new Error();
      }
    } catch (e: unknown) {
    }
  };

  useEffect(() => {
    if(error !== undefined && error !== 'desconocido'){
      toast.error('Error', `Ocurrió un error: ${error}`);
    } else if(error === 'desconocido')
      toast.error('Error', `Ocurrió un error: ${error}`);

  }, [error]);

  return (
    <Form<NewUnnamedLotFormInputs>
      name="basic"
      form={form}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      style={{ maxWidth: '100%', marginTop: '1rem' }}
      initialValues={{ remember: true }}
      onFinish={onSubmit}
      onFinishFailed={console.log}
      autoComplete="off"
    >
      <Typography.Title level={3}>Crear nuevo Lote Innominado</Typography.Title>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Número de Lote"
        name="lot_number"
        rules={[
          { required: true, message: 'Por favor ingresar un número de lote.' },
          { type: 'number', message: 'Ingrese un número por favor.' },
        ]}
      >
        <InputNumber controls={false} />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Tamaño de Lote"
        name="lot_size"
        rules={[
          { required: true, message: 'Por favor ingresar un tamaño de lote.' },
          { type: 'number', message: 'Ingrese un número por favor.' },
        ]}
      >
        <InputNumber controls={false} />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name="card_type"
        label="Tipo de tarjeta"
      >
        <Select
          placeholder="Seleccionar tipo de tarjeta"
          options={[
            {
              label: 'Fisica',
              value: CardType.Physical,
            },
            {
              label: 'Virtual',
              value: CardType.Virtual,
            },
          ]}
        />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Grupo de afinidad"
        name="card_affinity_group_id"
        rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
      >
        <Select
          placeholder="Seleccionar"
          showSearch
          filterOption={filterOption}
          loading={loading}
          options={affinityGroups?.map((affg) => ({ label: affg.name, value: affg.id }))}
        />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name="card_product_id"
        label="Producto"
        rules={[{ required: true, message: 'Seleccionar tipo de producto' }]}
      >
        <Select
          placeholder="Seleccionar producto"
          showSearch
          filterOption={filterOption}
          loading={cardProductsLoading}
          options={cardProducts?.map((cardProduct) => ({ label: cardProduct.name, value: cardProduct.name }))}
        />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Inicial de cuarta linea"
        name="initial_fourth_line"
        rules={[
          { type: 'number', message: 'Ingrese un numero por favor.' },
        ]}
      >
        <InputNumber controls={false} />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Tipo de Balance"
        name="balance_type"
        rules={[{ required: true, message: 'Por favor selecciona una opcion.' }]}
      >
        <Select
          placeholder="Seleccionar"
          options={Object.entries(CardBalanceType).map(([code, name]) => ({ label: CardBalanceTypeParser[name], value: code }))}
        />
      </Form.Item>
      <Typography.Title level={3}>Direccion de Envio</Typography.Title>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Id de seguimiento"
        name={['fulfillment', 'tracking_id']}
        rules={[
          { required: true, message: 'Por favor ingresar un número de seguimiento.' },
          { type: 'string', message: 'Ingrese un valor por favor.' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Codigo de distribucion del correo"
        name={['fulfillment', 'code_currier']}
        rules={[
          { required: true, message: 'Agregar codigo distribucion.' },
          { type: 'string', message: 'Ingrese un valor por favor.' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Calle"
        name={['fulfillment', 'shipping', 'street']}
        rules={[
          { required: true, message: 'Agregar calle.' },
          { type: 'string', message: 'Ingrese un valor por favor.' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Numero"
        name={['fulfillment', 'shipping', 'street_number']}
        rules={[
          { required: true, message: 'Agregar calle.' },
          { type: 'number', message: 'Ingrese un valor por favor.' },
        ]}
      >
        <InputNumber controls={false} />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name={['fulfillment', 'shipping', 'country']}
        label="Pais"
        rules={[{ required: true, message: 'Por favor ingresar pais.' }]}
      >
        <Select
          placeholder="Seleccionar pais"
          showSearch
          options={Object.entries(Countries)
            .filter(([code]) => !EXCLUDED_COUNTRIES.includes(code))
            .map(([code, name]) => ({ label: name, value: code }))}
        />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name={['fulfillment', 'shipping', 'state']}
        label="Estado"
        rules={[{ required: true, message: 'Por favor ingresar estado.' }]}
      >
        <Select
          placeholder="Seleccionar estado"
          showSearch
          options={Object.entries(StatesByCountry?.[country] ?? {}).map(([stateCode, name]) => ({
            label: name,
            value: stateCode,
          }))}
        />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        label="Ciudad"
        name={['fulfillment', 'shipping', 'city']}
        rules={[{ required: true, message: 'Por favor ingresar ciudad.' }]}
      >
        <Select
          placeholder="Seleccionar ciudad"
          showSearch
          options={CitiesByStateByCountry?.[country]?.[state]?.toSorted()?.map((name) => ({
            label: name,
            value: name,
          }))}
        />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name={['fulfillment', 'shipping', 'postal_code']}
        label="Codigo Postal"
        rules={[
          { required: true, message: 'Ingresar codigo postal.' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name={['fulfillment', 'shipping', 'floor']}
        label="Piso"
        rules={[
          { required: false, message: 'Ingresar nro de piso.' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name={['fulfillment', 'shipping', 'apt']}
        label="Departamento"
        rules={[
          { required: false, message: 'Ingresar nro de departament.' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item<NewUnnamedLotFormInputs>
        name={['fulfillment', 'shipping', 'extra_comment']}
        label="Comentarios extras"
      >
        <Input.TextArea allowClear />
      </Form.Item>

      <SubmitButton form={form} loading={isLoading} />
    </Form>
  )
}
const UnnamedFilters = ({ onSubmit }: { onSubmit: (data: UnnamedLotsFilterProps) => void }) => {
  const [form] = Form.useForm<UnnamedLotsFilterProps>();


  const onFinish = (values: UnnamedLotsFilterProps) => {
    onSubmit(values);
  };

  return (

    <Form
      form={form}
      onFinish={onFinish}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="rangeDates">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={4}></Col>
        <Col span={8} style={{ textAlign: 'right' }}>
          <Space>
            <Button htmlType="submit" type="primary">
              Buscar
            </Button>
            <Button htmlType="reset">Limpiar</Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
}

const UnnamedLots = () => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const [newUnnamedCardsModalOpen, setNewUnnamedCardsModalOpen] = useState<boolean>(false);

  const NewUnnamedLot = () => {
    return (
      <Button icon={<PlusOutlined />} onClick={() => setNewUnnamedCardsModalOpen(true)}  type="primary">
        Nuevo
      </Button>
    );
  };

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });

  const { unnamedLots, pageInfo, isLoading, execute } = useGetAllUnnamedLots(
    paginationModel.page,
    paginationModel.pageSize,
    filters.startDate,
    filters.endDate
  );

  const handleSearchClick = (data: UnnamedLotsFilterProps) => {
    setFilters({
      startDate: data.rangeDates?.[0] ? `${format(data.rangeDates?.[0]?.toDate(), 'yyyy-MM-dd')}T00:00:00` : '',
      endDate: data.rangeDates?.[1] ? `${format(data.rangeDates?.[1]?.toDate().setDate(data.rangeDates?.[1]?.toDate().getDate() + 1), 'yyyy-MM-dd')}T00:00:00` : '',
    });
  }

  const handleViewUnnamedLotDetailClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, lotNumber: string) => {
    e.preventDefault();
    navigate(`/${issuer}/unnamed-lots/${lotNumber}`);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  function getBalanceTypeDesign ( balanceType: CardBalanceType) {
    return balanceType === 'CENTRALIZED' ? <Tag color="green">{ CardBalanceTypeParser[balanceType] }</Tag> : balanceType === 'DECENTRALIZED' ? <Tag color="blue">{ CardBalanceTypeParser[balanceType] }</Tag> : '';
  }

  const columns: ColumnsType<IUnnamedLot> = [
    {
      key: 'lot_number',
      dataIndex: 'lot_number',
      title: 'Número de lote',
      render: (_, record) => {
        return record.lot_number ?? record.lot_number ?? 'Sin número de lote.';
      },
    },
    {
      key: 'lot_size',
      dataIndex: 'lot_size',
      title: 'Tamaño de lote',
      render: (_, record) => {
        return record.lot_size;
      },
    },
    {
      key: 'balance_type',
      dataIndex: 'balance_type',
      title: 'Tipo de balance',
      render: (_, record) => {
        return getBalanceTypeDesign(record.balance_type);
      }
    },
    {
      key: 'creation_date',
      dataIndex: ['creation_date'],
      title: 'Fecha de creación',
      render: (_, record) => format(new Date(record?.creation_date), 'dd-MM-yyyy HH:mm'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            onClick={(e) => handleViewUnnamedLotDetailClick(e, record.lot_number)}
            size="small"
            type="link"
            icon={<EyeOutlined />}
          >
            Ver
          </Button>
        </Space>
      ),
    },
  ];
  return (
    <PageWithFilters title="Innominadas">
      <PageWithFiltersContentContainer>
        <UnnamedFilters onSubmit={handleSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <div style={{ display: 'flex', marginBottom: '1.5rem', justifyContent: 'flex-end', gap: '1rem' }}>
          <NewUnnamedLot />
        </div>
        <Table
          dataSource={unnamedLots ?? []}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: pageInfo.total,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
      <Modal
        open={newUnnamedCardsModalOpen}
        onCancel={() => setNewUnnamedCardsModalOpen(false)}
        footer={null}
        width={500}
      >
        <NewUnnamedLotForm execute={() => execute() } onCancel={() => setNewUnnamedCardsModalOpen(false)} />
      </Modal>
    </PageWithFilters>
  );
}
export default withAuthorization([RealmRoles.CardsUnnamedRead, RealmRoles.CardsUnnamedWrite], UnnamedLots);